import { Routes } from '@angular/router';
import { LayoutComponent } from './layout/layout.component';
import { LoginComponent } from './auth/login/login.component';
import { AuthGuard } from './auth/auth.guard';
import {AdminGuard} from './auth/admin.guard';
import { OnboardingGuard } from './auth/onboarding.guard';
import { NoAccessComponent } from './no-access/no-access.component';
import { CAPABILITY } from './shared/utils/auth-groups.util';

/** Les onglets de l'ancien `/admin/admin-panel?tab=` et la page qui remplace chacun. */
const LEGACY_ADMIN_TABS: Record<string, string> = {
  GENERAL: '/admin/maintenance',
  USERS: '/admin/users',
  AUDIT: '/admin/audit-log',
  PERF: '/admin/performance',
  LEDGER: '/admin/ledger',
};

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        // Sans `requires` : l'accueil est ouvert à quiconque a une capacité,
        // quelle qu'elle soit. Ce sont ses WIDGETS qui se filtrent un à un.
        path: 'dashboard',
        loadComponent: () => import('./manager/manager-panel/manager-panel.component').then(m => m.ManagerPanelComponent),
        data: { title: 'Tableau de bord' }
      },
      {
        path: 'owners',
        data: { title: 'Propriétaires', requires: CAPABILITY.OWNER_MANAGE },
        children: [
          { path: '', loadComponent: () => import('./owner/owner.component').then(m => m.OwnerComponent) },
          { path: 'create-owner', loadComponent: () => import('./owner/create-owner/create-owner.component').then(m => m.CreateOwnerComponent) },
          {
            // Le formulaire ne sert plus qu'à CRÉER : un propriétaire existant
            // s'édite carte par carte sur sa fiche. Quatrième et dernière fiche
            // à faire cette bascule, après bail, bien et locataire.
            path: 'create-owner/:id',
            redirectTo: 'details-owner/:id',
            pathMatch: 'full'
          },
          { path: 'details-owner/:id', loadComponent: () => import('./owner/details-owner/details-owner.component').then(m => m.DetailsOwnerComponent) },
          // Un compte de gestion est un niveau SOUS la fiche, pas une modale :
          // le fil d'Ariane descend, l'URL se met en signet et le retour
          // navigateur fonctionne. Même convention de segment nommé que
          // `details-ticket/:id/edit`.
          {
            // Un compte de gestion est un écran comptable : il resserre sur
            // TRANSACTION_VIEW ce que `/owners` ouvre à l'exploitation.
            path: 'details-owner/:id/account-management/:accountId',
            loadComponent: () => import('./owner/details-owner/details-transaction-management/details-transaction-management.component').then(m => m.DetailsTransactionManagementComponent),
            data: { requires: CAPABILITY.TRANSACTION_VIEW }
          },
          // Même raison pour un compte BANCAIRE : c'était une modale au tableau
          // maison, sans tri ni URL. Le compte est désigné par son IBAN, seule
          // clé que porte `transaction.ledgeraccountiban`.
          {
            path: 'details-owner/:id/bank-account/:iban',
            loadComponent: () => import('./owner/details-owner/details-transaction/details-transaction.component').then(m => m.DetailsTransactionComponent),
            data: { requires: CAPABILITY.TRANSACTION_VIEW }
          },
          { path: 'invoicing/:id', loadComponent: () => import('./owner/invoicing/invoicing.component').then(m => m.InvoicingComponent), data: { requires: CAPABILITY.INVOICING_MANAGE } }
        ]
      },
      {
        path: 'tenants',
        data: { title: 'Locataires', requires: CAPABILITY.TENANT_MANAGE },
        canActivate: [AuthGuard],
        children: [
          { path: '', loadComponent: () => import('./tenant/tenant.component').then(m => m.TenantComponent) },
          { path: 'create-tenant', loadComponent: () => import('./tenant/create-tenant/create-tenant.component').then(m => m.CreateTenantComponent) },
          {
            // Le formulaire ne sert plus qu'à CRÉER : un locataire existant
            // s'édite carte par carte sur sa fiche. Même bascule que
            // `create-lease/:id` et `create-property/:id`.
            path: 'create-tenant/:id',
            redirectTo: 'details-tenant/:id',
            pathMatch: 'full'
          },
          { path: 'details-tenant/:id', loadComponent: () => import('./tenant/details-tenant/details-tenant.component').then(m => m.DetailsTenantComponent) }
        ]
      },
      {
        path: 'properties',
        data: { title: 'Biens', requires: CAPABILITY.PROPERTY_MANAGE },
        canActivate: [AuthGuard],
        children: [
          { path: '', loadComponent: () => import('./property/property.component').then(m => m.PropertyComponent) },
          { path: 'create-property', loadComponent: () => import('./property/create-property/create-property.component').then(m => m.CreatePropertyComponent) },
          {
            // Le formulaire ne sert plus qu'à CRÉER : un bien existant s'édite
            // carte par carte sur sa fiche. L'ancienne adresse d'édition y
            // renvoie plutôt que de disparaître — elle vit dans des signets et
            // dans d'anciens liens. Même bascule que `create-lease/:id`.
            path: 'create-property/:id',
            redirectTo: 'details-property/:id',
            pathMatch: 'full'
          },
          { path: 'details-property/:id', loadComponent: () => import('./property/details-property/details-property.component').then(m => m.DetailsPropertyComponent)}
        ]
      },
      {
        path: 'transactions',
        data: { title: 'Transactions', requires: CAPABILITY.TRANSACTION_VIEW },
        canActivate: [AuthGuard],
        children: [
          {
            path: '', loadComponent: () => import('./transactions/transactions.component').then(m => m.TransactionsComponent),
          },
          {
            path: 'splitTransactions/:id', loadComponent: () => import('./transactions/split-transactions/split-transactions.component').then(m => m.SplitTransactionsComponent),
            data: { requires: CAPABILITY.TRANSACTION_SPLIT }
          },
          {
            // Saisie manuelle d'extraits bancaires, pour les comptes sans
            // fichier camt. Aucune garde a ajouter : AuthGuard sur le parent
            // restreint deja /transactions a ADMIN et FINANCE.
            path: 'statements', loadComponent: () => import('./transactions/encode-statements/encode-statements.component').then(m => m.EncodeStatementsComponent),
            data: { requires: CAPABILITY.TRANSACTION_SPLIT }
          }
        ]
      },
      {
        path: 'managers',
        data: { title: 'Manager', requires: CAPABILITY.DIRECTORY_MANAGE },
        canActivate: [AuthGuard],
        children: [
          {
            path: '', loadComponent: () => import('./manager/manager.component').then(m => m.ManagerComponent)
          },
          {
            path: 'details-manager/:id', loadComponent: () => import('./manager/details-manager/details-manager.component').then(m => m.DetailsManagerComponent)
          }
        ]
      },
      {
        path: 'leases',
        data: { title: 'Baux', requires: CAPABILITY.LEASE_MANAGE },
        canActivate: [AuthGuard],
        children: [
          {
            path: '', loadComponent: () => import('./lease/lease.component'),
          },
          {
            path: 'create-lease',
            loadComponent: () => import('./lease/create-lease/create-lease.component').then(m => m.CreateLeaseComponent)
          },
          {
            // Le formulaire ne sert plus qu'à CRÉER : un bail existant s'édite
            // section par section sur sa fiche. L'ancienne adresse d'édition y
            // renvoie plutôt que de disparaître — elle vit dans des signets et
            // dans d'anciens liens.
            path: 'create-lease/:id',
            redirectTo: 'details-lease/:id',
            pathMatch: 'full'
          },
          {
            path: 'details-lease/:id',
            loadComponent: () => import('./lease/details-lease/details-lease.component').then(m => m.DetailsLeaseComponent)
          },
          {
            path: 'indexing-candidate-detail/:id',
            loadComponent: () => import('./lease/indexing-candidate-detail/indexing-candidate-detail.component').then(m => m.IndexingCandidateDetailComponent),
            data: { requires: CAPABILITY.INDEXATION_MANAGE }
          },
          {
            path: 'indexation-case/:id',
            loadComponent: () => import('./lease/indexation-case-detail/indexation-case-detail.component').then(m => m.IndexationCaseDetailComponent),
            data: { requires: CAPABILITY.INDEXATION_MANAGE }
          }
        ]
      },
      {
        path: 'todos',
        data: { title: 'Todos', requires: CAPABILITY.TODO_MANAGE },
        canActivate: [AuthGuard],
        children: [
          {
            path: '', loadComponent: () => import('./todo-list/todo-list.component').then(m => m.TodoListComponent)
          },
          {
            path: 'templates', loadComponent: () => import('./todo-list/template-manager/template-manager.component').then(m => m.TemplateManagerComponent)
          }
        ]
      },
      {
        // Jusqu'au 2026-10-13 une entrée du menu Finance ; parqué dans les
        // Paramètres (aperçu TEST) tant que l'import n'est pas en service.
        path: 'import-fast',
        redirectTo: '/admin/import-fast',
        pathMatch: 'full'
      },
      {
        path: 'rent-calls',
        data: { title: 'Appel de loyers', requires: CAPABILITY.RENT_CALL_MANAGE },
        canActivate: [AuthGuard],
        children: [
          {
            path: '', loadComponent: () => import('./rent-calls/rent-calls.component').then(m => m.RentCallsComponent)
          }
          ]
      },
      {
        path: 'invoices',
        data: { title: 'Facturation', requires: CAPABILITY.INVOICING_MANAGE },
        canActivate: [AuthGuard],
        children: [
          {
            path: '', loadComponent: () => import('./invoices/invoices.component').then(m => m.InvoicesComponent)
          }
          ]
      },
      {
        path: 'purchases',
        data: { title: 'Achats', requires: CAPABILITY.PURCHASE_ENCODE },
        canActivate: [AuthGuard],
        children: [
          {
            path: '', loadComponent: () => import('./purchases/purchases.component').then(m => m.PurchasesComponent)
          },
          {
            path: 'vendors', loadComponent: () => import('./purchases/vendors/vendors.component').then(m => m.VendorsComponent),
            data: { requires: CAPABILITY.VENDOR_MANAGE }
          },
          {
            path: 'details-purchase/:id', loadComponent: () => import('./purchases/details-purchase/details-purchase.component').then(m => m.DetailsPurchaseComponent)
          }
        ]
      },
      {
        path: 'payments',
        data: { title: 'Paiements', requires: CAPABILITY.PAYMENT_MANAGE },
        canActivate: [AuthGuard],
        children: [
          {
            path: '', loadComponent: () => import('./payments/payments.component').then(m => m.PaymentsComponent)
          },
          {
            path: ':id', loadComponent: () => import('./payments/payment-batch-detail/payment-batch-detail.component').then(m => m.PaymentBatchDetailComponent)
          }
        ]
      },
      {
        path: 'manager',
        children: [
          // Ancienne adresse du tableau de bord : les signets y mènent encore,
          // et c'était `MANAGER_HOME`. `pathMatch: 'full'` pour ne pas happer
          // les routes sœurs.
          { path: 'manager-panel', redirectTo: '/dashboard', pathMatch: 'full' },
          {
            path: 'tickets',
            canActivate: [AuthGuard],
            // « Service » depuis le 2026-09-03 : l'écran porte deux onglets, l'Inbox
            // et les Interventions. Le segment reste `tickets` — les sous-écrans et
            // les signets en dépendent, et la table s'appelle toujours `ticket`.
            data: { title: 'Service', requires: CAPABILITY.TICKET_MANAGE },
            children: [
              // La coquille à onglets. L'onglet actif est porté par `?tab=`.
              { path: '', loadComponent: () => import('./manager/service/manager-service.component').then(m => m.ManagerServiceComponent) },
              // Création RAPIDE. Elle n'édite plus : cinq champs, puis la fiche.
              { path: 'create-ticket', loadComponent: () => import('./manager/tickets/create-ticket/create-ticket.component').then(m => m.CreateTicketComponent) },
              // `details-ticket/:id` et non `:id`, par convention du dépôt
              // (`details-owner/:id`, `details-lease/:id`…) : un segment nu
              // entrerait en concurrence avec `create-ticket`.
              //
              // Les deux routes suivantes pointent le MÊME composant : l'édition
              // est un mode de la fiche, dans la même mise en page. Angular le
              // recrée en changeant de route, donc chaque bascule repart de l'état
              // persisté — ce qui est voulu dans les deux sens.
              { path: 'details-ticket/:id', loadComponent: () => import('./manager/tickets/details-ticket/details-ticket.component').then(m => m.DetailsTicketComponent) },
              {
                path: 'details-ticket/:id/edit',
                loadComponent: () => import('./manager/tickets/details-ticket/details-ticket.component').then(m => m.DetailsTicketComponent),
                data: { editing: true }
              },
              // Ancienne URL d'édition : les signets aboutissent au nouvel écran.
              // `redirectTo` reporte les segments de même nom, `:id` est transmis.
              { path: 'create-ticket/:id', redirectTo: 'details-ticket/:id/edit' }
            ]
          },
          // `leases` a vécu ici du 2026-08-31 au 2026-09-14 : une copie de
          // `/leases` sûre pour un gestionnaire qui n'y avait pas accès. Il y a
          // accès désormais, la copie est partie. La redirection garde les
          // signets et les liens du tableau de bord d'avant.
          { path: 'leases', redirectTo: '/leases', pathMatch: 'full' }
        ]
      },
      {
        // Les Paramètres, comme ceux de LP-FIN-APP : une vue
        // d'ensemble en cartes, puis une page par sujet
        // (knowledge/plateforme/administration.md).
        path: 'admin',
        canActivate: [AuthGuard, AdminGuard],
        data: { requires: CAPABILITY.ADMIN_PANEL },
        children: [
          { path: '', pathMatch: 'full', loadComponent: () => import('./admin/admin-overview/admin-overview.component').then(m => m.AdminOverviewComponent), data: { title: 'Paramètres' } },
          { path: 'users', loadComponent: () => import('./admin/admin-panel/users/users.component').then(m => m.UsersComponent), data: { title: 'Utilisateurs' } },
          { path: 'audit-log', loadComponent: () => import('./admin/admin-panel/audit-log/audit-log.component').then(m => m.AuditLogComponent), data: { title: 'Journal d’audit' } },
          { path: 'performance', loadComponent: () => import('./admin/admin-panel/performance/performance.component').then(m => m.PerformanceComponent), data: { title: 'Performance' } },
          { path: 'ledger', loadComponent: () => import('./admin/admin-panel/ledger/ledger.component').then(m => m.LedgerComponent), data: { title: 'Comptabilité' } },
          { path: 'maintenance', loadComponent: () => import('./admin/admin-panel/general/general.component').then(m => m.GeneralComponent), data: { title: 'Maintenance' } },
          { path: 'bank-account-cleanup', loadComponent: () => import('./admin/bank-account-cleanup/bank-account-cleanup.component').then(m => m.BankAccountCleanupComponent), data: { title: 'Doublons d’IBAN' } },
          { path: 'import-fast', loadComponent: () => import('./import-fast/import-fast.component').then(m => m.ImportFastComponent), data: { title: 'Imports F.A.S.T' } },
          {
            // L'écran à cinq onglets qui a été les Paramètres jusqu'au
            // 2026-10-13 : chaque onglet a sa page, les signets suivent.
            path: 'admin-panel',
            pathMatch: 'full',
            redirectTo: ({ queryParams }) => LEGACY_ADMIN_TABS[String(queryParams['tab'] ?? '')] ?? '/admin'
          }
        ]
      }
    ]
  },
  {
    path: 'login',
    component: LoginComponent,
    data: { title: 'Login' }
  },
  {
    path:'onboarding',
    loadComponent: () => import('./onboarding/onboarding.component').then(m => m.OnboardingComponent),
    canActivate: [OnboardingGuard],
    data: { title: 'Onboarding' }
  },
  // Hors du `LayoutComponent`, donc sans sidebar : un utilisateur qui atterrit
  // ici n'a par définition aucun espace à afficher. Sans garde non plus — la
  // page ne lit aucune donnée, et la protéger ne ferait que créer une boucle
  // avec les gardes qui y renvoient.
  {
    path: 'no-access',
    component: NoAccessComponent,
    data: { title: 'Accès en attente' }
  }
];
